import React, { useRef, useState, useEffect } from "react";
import styles from "./ChildCutoffContainer.module.scss";
import classnames from "classnames";
import map from "lodash/map";

interface ChildCutoffContainerProps {
  children: React.ReactNode;
  rootClassName?: string;
  childContainerClassName?: string;
}

export default function ChildCutoffContainer({
  children,
  rootClassName,
  childContainerClassName,
}: ChildCutoffContainerProps): React.JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const childRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleChildrenCount, setVisibleChildrenCount] = useState<number>(0);
  const notVisibleChildrenCount =
    React.Children.count(children) - visibleChildrenCount;

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      calculateVisibleChildren(entries[0].contentRect.width);
    });

    if (rootRef.current) {
      observer.observe(rootRef.current);
    }

    return () => {
      if (rootRef.current) {
        observer.unobserve(rootRef.current);
      }
    };
  }, [children]);

  const calculateVisibleChildren = (rootWidth: number): void => {
    let totalWidth = 0;
    let count = 0;

    for (let i = 0; i < childRefs.current.length; i++) {
      const child = childRefs.current[i];
      if (child) {
        const style = window.getComputedStyle(child);
        const marginLeft = parseFloat(style.marginLeft) || 0;
        const marginRight = parseFloat(style.marginRight) || 0;
        const fullWidth =
          child.getBoundingClientRect().width + marginLeft + marginRight;

        if (totalWidth + fullWidth <= rootWidth) {
          totalWidth += fullWidth;
          count++;
        } else {
          break;
        }
      }
    }

    setVisibleChildrenCount(count);
  };

  return (
    <div
      className={classnames(
        rootClassName,
        "flex-row flex-center width-100-percent",
      )}
    >
      <div
        ref={rootRef}
        className={classnames(childContainerClassName, styles.root)}
      >
        {/* map expects array for proper typing */}
        {map(React.Children.toArray(children), (child, index) => {
          const isHidden = index >= visibleChildrenCount;
          return (
            <div
              key={index}
              className={classnames(styles.childElementContainer, {
                [styles.hidden]: isHidden,
              })}
              ref={(el) => {
                childRefs.current[index] = el;
              }}
            >
              {child}
            </div>
          );
        })}
      </div>

      {notVisibleChildrenCount > 0 && (
        <div className={styles.notVisibleIndicator}>
          <span className="text-sm">+{notVisibleChildrenCount} more</span>
        </div>
      )}
    </div>
  );
}
